import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Bandeja de mensajes (Etapa 1, 2026-08-19) ────────────────────────────────
// Lee de las mismas tablas que la bitácora del contacto: una sola fuente.

export type ThreadState = 'unanswered' | 'answered' | 'expiring';
/** Lo que de verdad hay que contestar: sin responder Y sin leer. Se calcula
 *  sobre dos cosas, no es un estado más del hilo. */
export type FiltroDeBandeja = ThreadState | 'pending';

export interface InboxThread {
  id: number;
  channelId: number;
  platform: string;
  /** 'whatsapp' | 'dm' | 'email' | 'form'. */
  channelType: string;
  /** Si por este canal se puede escribir. Un formulario se lee y no se responde. */
  canReply: boolean;
  senderDisplayName: string | null;
  identifier: string;
  contactId: number | null;
  contactName: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageDirection: 'inbound' | 'outbound' | null;
  state: ThreadState;
  windowExpiresAt: string | null;
  hoursLeft: number | null;
  /** Cuándo alguien dijo "esta la llevo yo". Mientras no sea null, el
   *  asistente no responde en este hilo, sin importar el nivel del canal. */
  takenOverAt: string | null;
  takenOverBy: number | null;
  /** Si la miraste. Distinto de `state`: "sin responder" dice que falta
   *  contestar, esto dice que la abriste. Es lo que marca la barra cian. */
  isRead: boolean;
  /** Dada por cerrada a mano: apaga el aviso de la fila y la saca de
   *  Pendientes. Distinto de `isRead`, que solo dice que la abriste. */
  isHandled: boolean;
  /** Cuándo se archivó. null = activa. */
  archivedAt: string | null;
}

/** Un archivo que vino con el mensaje: hoy, una nota de voz de WhatsApp. */
export interface InboxAttachment {
  id: number;
  kind: string;
  mimeType: string | null;
  /** null mientras el archivo todavía no se bajó de 360dialog. */
  url: string | null;
  voice: boolean;
  sizeBytes: number | null;
  filename: string | null;
  error: string | null;
}

export interface InboxMessage {
  id: number;
  direction: 'inbound' | 'outbound';
  body: string;
  origin: string | null;
  status: string | null;
  sendError: string | null;
  at: string;
  attachments: InboxAttachment[];
  /** Su id de WhatsApp — con esto se lo puede citar al responder. Null en un
   *  correo, o en un borrador que todavía no salió. */
  platformMessageId: string | null;
  /** El mensaje que este cita, ya resuelto por el servidor. Null cuando no
   *  cita a ninguno, y también cuando el citado no está en nuestra base. */
  replyTo: { id: number; direction: 'inbound' | 'outbound'; body: string } | null;
}

export interface ThreadProposal {
  id: number;
  taskId: string;
  turnGroupId: string;
  toolName: string;
  verb: string;
  sentence: string;
  irreversible: boolean;
  warning: string | null;
  createdAt: string;
  status: string;
}

export interface InboxThreadDetail extends InboxThread {
  messages: InboxMessage[];
  proposals: ThreadProposal[];
}

export interface InboxList {
  threads: InboxThread[];
  counts: Record<FiltroDeBandeja, number>;
  /** Cuántas archivadas hay, para el contador de la categoría. */
  archivedCount: number;
  platforms: string[];
}

export function useDmInbox(platform: string, state: string, archivados = false) {
  const qs = new URLSearchParams();
  if (platform) qs.set('platform', platform);
  if (state) qs.set('state', state);
  // 'archived' es una categoría aparte, no un estado: lo archivado no sale en
  // ninguna otra vista, ni siquiera con el filtro en "Todos".
  if (archivados) qs.set('scope', 'archived');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<InboxList>({
    queryKey: ['dm-inbox', platform, state, archivados],
    queryFn: () => api.get(`/api/dm/threads${suffix}`),
    // Cada 10 segundos, y también al volver a la pestaña.
    //
    // Eran 60s, pensados para que la cuenta regresiva de la ventana de 24h no
    // se quedara quieta. Pero esta lista es lo que alguien mira MIENTRAS le
    // escriben: un mensaje que entra por webhook está en la base al instante y
    // tardaba hasta un minuto en subir a la primera fila, que es tiempo
    // suficiente para creer que no llegó.
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

export function useDmThread(id: number | null) {
  return useQuery<InboxThreadDetail>({
    queryKey: ['dm-thread', id],
    queryFn: () => api.get(`/api/dm/threads/${id}`),
    enabled: id != null,
    // La conversación abierta es donde se espera la respuesta del otro lado.
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export interface TakeoverState {
  threadId: number;
  takenOverAt: string | null;
  takenOverBy: number | null;
}

/** Tomar y devolver invalidan el hilo Y la lista: el estado se ve en los dos
 *  lados y dejarlos desincronizados haría que la lista mostrara como libre
 *  algo que se acaba de tomar. */
function useTakeoverMutation(metodo: 'post' | 'delete') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: number) =>
      api[metodo]<TakeoverState>(`/api/dm/threads/${threadId}/takeover`),
    onSuccess: (_d, threadId) => {
      void qc.invalidateQueries({ queryKey: ['dm-thread', threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
    },
  });
}

export const useTakeOverThread = () => useTakeoverMutation('post');
export const useReleaseThread = () => useTakeoverMutation('delete');

/** Aprobar o rechazar una propuesta desde el hilo. Usa los MISMOS endpoints
 *  que la cola de tareas — una propuesta decidida acá tiene que desaparecer
 *  allá, y al revés. */
function useProposalDecision(accion: 'approve' | 'reject') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string; threadId: number }) =>
      api.post<{ ok: boolean }>(`/api/tasks/${taskId}/${accion}`, {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dm-thread', vars.threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export const useApproveThreadProposal = () => useProposalDecision('approve');
export const useRejectThreadProposal = () => useProposalDecision('reject');

/** Un documento de la plataforma que se le puede pasar al contacto. */
export interface DocumentoEnlazable {
  tipo: 'estimate' | 'invoice' | 'report';
  id: number;
  titulo: string;
  estado: string | null;
  url: string;
  fecha: string | null;
}

/** Se pide solo cuando se abre el selector: son tres consultas y nadie las
 *  necesita hasta que va a mandar algo. */
export function useDocumentosDelHilo(threadId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['dm-documentos', threadId],
    queryFn: () => api.get<{ documentos: DocumentoEnlazable[] }>(`/api/dm/threads/${threadId!}/documentos`),
    enabled: enabled && threadId != null,
  });
}

/** Un archivo subido que todavía no se mandó. */
export interface AdjuntoSubido {
  id: number;
  kind: string;
  filename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
}

/** Sube un archivo para mandarlo en la conversación. Va por FormData, no por
 *  JSON: el archivo son bytes, no un campo más del cuerpo. */
export function useSubirAdjunto() {
  return useMutation({
    mutationFn: async (file: File): Promise<AdjuntoSubido> => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<AdjuntoSubido>('/api/dm/attachments', form);
    },
  });
}

export function useDescartarAdjunto() {
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/dm/attachments/${id}`),
  });
}

/** Responder por correo. El destinatario, la casilla, el asunto y los
 *  encabezados del hilo los arma el servidor: acá solo viaja lo que se escribió. */
export function useResponderPorCorreo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: number; body: string }) =>
      api.post<InboxThreadDetail>(`/api/dm/threads/${threadId}/reply-email`, { body }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dm-thread', vars.threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
    },
  });
}

export function useSendDmReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body, attachmentIds, replyToPlatformId }: {
      threadId: number; body: string; attachmentIds?: number[];
      /** Cita a un mensaje puntual de la conversación, por su id de WhatsApp. */
      replyToPlatformId?: string | null;
    }) =>
      api.post<InboxThreadDetail>(`/api/dm/threads/${threadId}/reply`, {
        body, attachmentIds, replyToPlatformId,
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dm-thread', vars.threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
    },
  });
}

// ── Leído y archivado ────────────────────────────────────────────────────────
// Las dos invalidan la lista, el hilo y los indicadores del menú: el mismo
// estado se ve en los tres lados y dejarlos desincronizados haría que la lista
// mostrara como sin leer algo que se acaba de marcar.

function useThreadFlagMutation<T extends Record<string, unknown>>(ruta: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: number[] } & T) =>
      api.post<{ updated: number }>(`/api/dm/threads/${ruta}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
      void qc.invalidateQueries({ queryKey: ['dm-thread'] });
      void qc.invalidateQueries({ queryKey: ['nav-badges'] });
    },
  });
}

/** "Esto ya está". Marca atendida sin obligar a escribir una respuesta. */
export function useSetThreadsHandled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, handled }: { ids: number[]; handled: boolean }) =>
      api.post<{ updated: number }>('/api/dm/threads/handled', { ids, handled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
      void qc.invalidateQueries({ queryKey: ['dm-thread'] });
      void qc.invalidateQueries({ queryKey: ['nav-badges'] });
    },
  });
}

export function useSetThreadsRead() {
  return useThreadFlagMutation<{ read: boolean }>('read');
}

export function useSetThreadsArchived() {
  return useThreadFlagMutation<{ archived: boolean }>('archive');
}

// ── Mensajes con hora puesta ─────────────────────────────────────────────────

export interface MensajeProgramado {
  id: number;
  threadId: number;
  /** ISO. */
  scheduledFor: string;
  body: string | null;
  templateId: number | null;
  valores: string[] | null;
  adjuntos: number;
}

/** Los que todavía no salieron en esta conversación, del más próximo al más
 *  lejano. Se refresca solo cada minuto: uno que ya salió deja de estar, y sin
 *  esto la lista seguiría mostrándolo como pendiente. */
export function useProgramados(threadId: number | null) {
  return useQuery({
    queryKey: ['dm-programados', threadId],
    queryFn: () => api.get<{ scheduled: MensajeProgramado[] }>(`/api/dm/threads/${threadId}/scheduled`),
    enabled: threadId != null,
    refetchInterval: 60_000,
  });
}

export function useProgramarMensaje() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, ...resto }: {
      threadId: number; body?: string; attachmentIds?: number[];
      replyToPlatformId?: string | null; templateId?: number; valores?: string[];
      scheduledFor: string;
    }) => api.post<MensajeProgramado>(`/api/dm/threads/${threadId}/schedule`, resto),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dm-programados', vars.threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-thread', vars.threadId] });
    },
  });
}

export function useCancelarProgramado(threadId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/dm/scheduled/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dm-programados', threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-thread', threadId] });
    },
  });
}
